import { ProductPagination, ProductStatusBadge } from "@/components";
import { Alert, Button, Empty, Input, Select, Skeleton, Table, Tooltip } from "antd";
import type { ColumnsType } from "antd/es/table";
import dayjs from "dayjs";
import { MapPinIcon, PlusIcon, RefreshCwIcon, RotateCcwIcon, SearchIcon, UsersRoundIcon } from "lucide-react";
import { useCallback, useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import {
  PortalTripRequestFilters,
  PortalTripRequestStatus,
  PortalTripRequestSummary,
  usePortalTripRequestsQuery,
} from "../actions/itinerary.actions";
import { PortalPageHeader, PortalPageShell } from "../components/PortalProduct";
import { getPortalPaths } from "../lib/routes";

const requestStatuses: PortalTripRequestStatus[] = ["draft", "returned", "submitted", "confirmed", "cancelled"];
const statusOptions = [
  { value: "draft", label: "Saved" },
  { value: "returned", label: "Returned" },
  { value: "submitted", label: "Confirmed" },
  { value: "confirmed", label: "Booking started" },
  { value: "cancelled", label: "Cancelled" },
];

const statusTone = (key: string): "gold" | "neutral" | "success" | "danger" => {
  if (key === "confirmed") return "success";
  if (key === "cancelled") return "danger";
  if (key === "submitted" || key === "returned") return "gold";
  return "neutral";
};

const formatDate = (value: string | null) => (value ? dayjs(value).format("DD MMM YYYY") : "Not set");
const formatMoney = (amount: number, currency: string) =>
  new Intl.NumberFormat("en-IN", { style: "currency", currency, maximumFractionDigits: 0 }).format(amount);

const parseStatus = (value: string | null): PortalTripRequestStatus | undefined =>
  requestStatuses.includes(value as PortalTripRequestStatus) ? (value as PortalTripRequestStatus) : undefined;

/**
 * Lists the agency's quotes grouped by request: one row per request number, with the number of
 * quote options inside it. Opening a row shows every option for that request.
 */
const PortalQuotesPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [filters, setFilters] = useState<PortalTripRequestFilters>(() => ({
    page: 1,
    limit: 20,
    sortOrder: "DESC",
    status: parseStatus(searchParams.get("status")),
    search: searchParams.get("search")?.trim().slice(0, 120) || undefined,
  }));
  const [search, setSearch] = useState(filters.search || "");
  const query = usePortalTripRequestsQuery(filters);
  const paths = getPortalPaths();

  const updateFilters = (patch: Partial<PortalTripRequestFilters>) => {
    setFilters((current) => ({ ...current, ...patch, page: patch.page ?? 1 }));
    if ("status" in patch) {
      const next = new URLSearchParams(searchParams);
      if (patch.status) next.set("status", patch.status);
      else next.delete("status");
      setSearchParams(next, { replace: true });
    }
  };

  const clearFilters = () => {
    setSearch("");
    setFilters({ page: 1, limit: 20, sortOrder: "DESC" });
    setSearchParams({}, { replace: true });
  };

  const openRequest = useCallback((id: string) => navigate(paths.request(id)), [navigate, paths]);

  const columns: ColumnsType<PortalTripRequestSummary> = useMemo(
    () => [
      {
        title: "Request",
        dataIndex: "reference",
        key: "reference",
        width: 120,
        render: (reference: string, request) => (
          <Link
            to={paths.request(request.id)}
            onClick={(event) => event.stopPropagation()}
            aria-label={`Open request ${reference}`}
            className="block font-semibold text-[#131B2A] no-underline hover:text-(--brand-primary)"
          >
            {reference}
          </Link>
        ),
      },
      {
        title: "Destination",
        key: "destination",
        width: 240,
        render: (_, request) => (
          <span className="flex items-center gap-2 font-medium text-[#131B2A]">
            <MapPinIcon size={15} className="shrink-0 text-gray-400" />
            {request.summary?.destinations.join(", ") || "No active quote"}
          </span>
        ),
      },
      {
        title: "Travel dates",
        key: "dates",
        width: 205,
        render: (_, request) =>
          request.summary ? (
            <div>
              <div>
                {formatDate(request.summary.startDate)} – {formatDate(request.summary.endDate)}
              </div>
              <div className="text-xs text-gray-500">{request.summary.nights} nights</div>
            </div>
          ) : (
            "–"
          ),
      },
      {
        title: "Travellers",
        key: "travellers",
        width: 130,
        render: (_, request) =>
          request.summary ? (
            <span className="flex items-center gap-1.5">
              <UsersRoundIcon size={14} /> {request.summary.travellers.total}
            </span>
          ) : (
            "–"
          ),
      },
      {
        title: "Quotes",
        dataIndex: "optionCount",
        key: "optionCount",
        width: 90,
        align: "right",
        render: (count: number) => <span className="tabular-nums">{count}</span>,
      },
      {
        title: "Value",
        key: "value",
        width: 145,
        align: "right",
        render: (_, request) =>
          request.summary?.totalPrice ? (
            <span className="font-semibold tabular-nums text-[#131B2A]">
              {formatMoney(request.summary.totalPrice.amount, request.summary.totalPrice.currency)}
            </span>
          ) : (
            "–"
          ),
      },
      {
        title: "Status",
        key: "status",
        width: 150,
        render: (_, request) => (
          <ProductStatusBadge tone={statusTone(request.requestStatus.key)}>{request.requestStatus.label}</ProductStatusBadge>
        ),
      },
      {
        title: "Updated",
        key: "updatedAt",
        width: 130,
        render: (_, request) => formatDate(request.updatedAt),
      },
      {
        title: "Guest name",
        dataIndex: "guestName",
        key: "guestName",
        width: 190,
        render: (guestName: string) => (
          <span className="block truncate text-[#131B2A]" title={guestName || undefined}>
            {guestName || "Unnamed guest"}
          </span>
        ),
      },
    ],
    [paths],
  );

  return (
    <PortalPageShell>
      <div className="space-y-3">
        <PortalPageHeader
          title="Quotes"
          meta={`${(query.data?.pagination.total || 0).toLocaleString()} requests`}
          description="Each request groups all of its quote options. Open a request to see them."
          actions={
            <>
              <Tooltip title="Refresh quotes">
                <Button
                  aria-label="Refresh quotes"
                  icon={<RefreshCwIcon size={15} />}
                  loading={query.isFetching}
                  onClick={() => void query.refetch()}
                />
              </Tooltip>
              <Link to={paths.newRequest}>
                <Button type="primary" icon={<PlusIcon size={15} />}>
                  Create with AI
                </Button>
              </Link>
            </>
          }
        />

        <section
          aria-label="Quote filters"
          className="rounded-lg border border-gray-200 bg-white p-2 shadow-[0_1px_2px_rgba(19,27,42,0.04)]"
        >
          <div className="grid gap-2 md:grid-cols-[minmax(0,1fr)_220px_auto_auto]">
            <Input
              allowClear
              value={search}
              prefix={<SearchIcon size={15} className="text-gray-400" />}
              placeholder="Request number or guest name"
              aria-label="Search quotes"
              onChange={(event) => setSearch(event.target.value)}
              onPressEnter={() => updateFilters({ search: search.trim() || undefined })}
            />
            <Select
              allowClear
              placeholder="Status"
              aria-label="Filter by status"
              value={filters.status}
              onChange={(value) => updateFilters({ status: value })}
              options={statusOptions}
            />
            <Button icon={<SearchIcon size={14} />} onClick={() => updateFilters({ search: search.trim() || undefined })}>
              Apply search
            </Button>
            <Button type="text" icon={<RotateCcwIcon size={14} />} onClick={clearFilters}>
              Clear filters
            </Button>
          </div>
        </section>

        {query.isError ? (
          <Alert
            type="error"
            showIcon
            message="Unable to load quotes"
            description="Please try again."
            action={
              <Button size="small" onClick={() => query.refetch()}>
                Retry
              </Button>
            }
          />
        ) : null}

        <section className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-[0_1px_2px_rgba(19,27,42,0.04)]">
          <div className="hidden md:block">
            <Table
              rowKey="id"
              columns={columns}
              dataSource={query.data?.items || []}
              loading={query.isLoading || query.isFetching}
              pagination={false}
              size="middle"
              scroll={{ x: 1300 }}
              onRow={(request) => ({
                onClick: (event) => {
                  if ((event.target as HTMLElement).closest("button, a")) return;
                  openRequest(request.id);
                },
              })}
              rowClassName="cursor-pointer transition-colors duration-150 hover:!bg-[var(--brand-primary-soft)]"
              locale={{
                emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="No quotes match these filters" />,
              }}
            />
          </div>
          <div className="space-y-3 p-3 md:hidden">
            {query.isLoading ? Array.from({ length: 3 }, (_, index) => <Skeleton key={index} active />) : null}
            {!query.isLoading && !query.data?.items.length ? <Empty description="No quotes match these filters" /> : null}
            {query.data?.items.map((request) => (
              <button
                key={request.id}
                type="button"
                onClick={() => openRequest(request.id)}
                className="w-full rounded-lg border border-gray-200 bg-white p-4 text-left transition-colors hover:border-(--brand-primary) hover:bg-[var(--brand-primary-soft)]"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold text-[#131B2A]">{request.reference}</div>
                    <div className="mt-0.5 text-xs text-gray-500">
                      {request.optionCount} quote{request.optionCount === 1 ? "" : "s"} · {request.guestName || "Unnamed guest"}
                    </div>
                    <div className="mt-1 flex items-center gap-1.5 text-sm text-gray-600">
                      <MapPinIcon size={14} /> {request.summary?.destinations.join(", ") || "No active quote"}
                    </div>
                  </div>
                  <ProductStatusBadge tone={statusTone(request.requestStatus.key)}>
                    {request.requestStatus.label}
                  </ProductStatusBadge>
                </div>
              </button>
            ))}
          </div>
          {query.data?.pagination.total ? (
            <ProductPagination
              current={query.data.pagination.page}
              pageSize={query.data.pagination.limit}
              total={query.data.pagination.total}
              itemLabel="requests"
              onChange={(page, pageSize) => setFilters((current) => ({ ...current, page, limit: pageSize }))}
              onPageSizeChange={(limit) => setFilters((current) => ({ ...current, page: 1, limit }))}
            />
          ) : null}
        </section>
      </div>
    </PortalPageShell>
  );
};

export default PortalQuotesPage;
